import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { format, parse, isValid } from 'date-fns';
import type { Mode, Project, Session, Task } from '@timato/shared';
import { Check } from 'lucide-react';
import { Button, DateField, Field, FieldButton, Input, Modal, SegmentedControl, TimeField, Tooltip, type Segment } from '@/components/ui';
import { GoalField } from './GoalField';
import { ProjectChip } from '@/components/ProjectChip';
import { LabelPicker } from '@/components/labels';
import { findOrCreateTask } from '@/data/tasks';
import { createManualSession, findOverlaps, shortenToFit, trimAround, updateSession, type SessionInput } from '@/data/sessions';
import { formatDuration, parseDuration } from '@/lib/time';
import { timeHM } from '@/lib/dates';
import { useFormats } from '@/lib/formats';
import { cn } from '@/lib/cn';

type Kind = 'focus' | 'track' | 'break';
// Track leads: time logged by hand is nearly always tracked time, rarely a pomodoro that went unrecorded.
const KINDS: Segment<Kind>[] = [{ value: 'track', label: 'Track' }, { value: 'focus', label: 'Focus' }, { value: 'break', label: 'Break' }];
const kindOf = (m: Mode): Kind => (m === 'short' || m === 'long' ? 'break' : m);
const modeOf = (k: Kind): Mode => (k === 'break' ? 'short' : k);

type Props = { open: boolean; onOpenChange: (o: boolean) => void; session?: Session; projects: Project[]; defaultDate?: Date };

/** Everything the form holds, as typed: the shape a dismissed Add session is kept in. */
type Draft = { goal: string; taskId: string | null; projectId: string | null; kind: Kind; date: string; startTime: string; endTime: string; length: string; labelIds: string[] };

/**
 * An Add session closed by Esc, a click outside or the X keeps what was typed until Save or Cancel: a stray click
 * next to the dialog must not cost a half-logged session. In memory only, so it survives switching screens, not a reload.
 */
let addDraft: Draft | null = null;

const draftOf = (session: Session): Draft => ({
  goal: session.goalText, taskId: session.taskId, projectId: session.projectId, kind: kindOf(session.mode),
  date: format(new Date(session.startedAt), 'yyyy-MM-dd'), startTime: format(new Date(session.startedAt), 'HH:mm'),
  endTime: format(new Date(session.endedAt), 'HH:mm'), length: formatDuration(session.durationSec, { zero: '' }), labelIds: session.labelIds,
});
const DAY_MS = 86_400_000;

const at = (date: string, hm: string) => { const d = parse(`${date} ${hm}`, 'yyyy-MM-dd HH:mm', new Date()); return isValid(d) ? d : null; };
/** An end on the start's day, or the next day's when it is not after the start (a session across midnight). */
const endAfter = (start: Date, date: string, hm: string) => { const e = at(date, hm); return e && e <= start ? new Date(e.getTime() + DAY_MS) : e; };
const lengthOf = (start: Date, end: Date) => formatDuration((end.getTime() - start.getTime()) / 1000, { zero: '' });

const blankDraft = (start: Date): Draft => ({ goal: '', taskId: null, projectId: null, kind: 'track', date: format(start, 'yyyy-MM-dd'), startTime: format(start, 'HH:mm'), endTime: '', length: '', labelIds: [] });

/** Add / Edit session (8c): goal + project, date & start, end OR length (the other follows), type, overlap resolution. */
export function SessionDialog({ open, ...rest }: Props) {
  return open ? <SessionForm key={rest.session?.id ?? 'new'} {...rest} /> : null;
}

function SessionForm({ onOpenChange, session, projects, defaultDate = new Date() }: Omit<Props, 'open'>) {
  const [init] = useState<Draft>(() => (session ? draftOf(session) : addDraft ?? blankDraft(new Date(new Date(defaultDate).setSeconds(0, 0)))));
  const [goal, setGoal] = useState(init.goal);
  // The task the session belongs to while the goal is still that task's: the one it was logged against, or a picked
  // suggestion. Typing or another project lets go of it, and saving finds or makes the task by name instead.
  const [taskId, setTaskId] = useState(init.taskId);
  const [project, setProject] = useState<Project | null>(projects.find((p) => p.id === init.projectId) ?? null);
  const [kind, setKind] = useState<Kind>(init.kind);
  const [date, setDate] = useState(init.date);
  const [startTime, setStartTime] = useState(init.startTime);
  const [endTime, setEndTime] = useState(init.endTime);
  const [length, setLength] = useState(init.length);
  const [labelIds, setLabelIds] = useState<string[]>(init.labelIds);
  const [now, setNow] = useState(() => new Date());
  // Whether End and Length hold the checkmark's "until now" rather than something typed.
  const [fromNow, setFromNow] = useState(false);
  const [error, setError] = useState<string>();
  const { time } = useFormats();
  // A project's labels are what its sessions carry (project dialog: "Sessions logged to this project inherit them"),
  // so changing the project replaces the labels rather than leaving another project's behind.
  const changeProject = (p: Project | null) => { setProject(p); setLabelIds(p ? [...p.labelIds] : []); setTaskId(null); };
  const typeGoal = (text: string) => { setGoal(text); setTaskId(null); };
  // A picked task brings its project and the labels the timer would log it with (8e): the task's plus the project's.
  const pickTask = (t: Task) => {
    const p = projects.find((x) => x.id === t.projectId) ?? null;
    setGoal(t.name); setProject(p); setTaskId(t.id);
    setLabelIds([...new Set([...t.labelIds, ...(p?.labelIds ?? [])])]);
  };

  const start = useMemo(() => at(date, startTime), [date, startTime]);
  const end = useMemo(() => {
    if (!start) return null;
    if (endTime) return endAfter(start, date, endTime);
    const sec = length ? parseDuration(length) : null;
    return sec ? new Date(start.getTime() + sec * 1000) : null;
  }, [start, date, endTime, length]);

  // Fill either end or length - the other follows (8c copy).
  const onEndChange = (v: string) => { setEndTime(v); setFromNow(false); const e = start && v ? endAfter(start, date, v) : null; if (start && e) setLength(lengthOf(start, e)); };
  const onLengthChange = (v: string) => { setLength(v); setFromNow(false); const sec = parseDuration(v); if (start && sec) setEndTime(format(new Date(start.getTime() + sec * 1000), 'HH:mm')); };
  const moveStart = (nextDate: string, nextTime: string) => {
    setDate(nextDate); setStartTime(nextTime); setNow(new Date());
    // The checkmark's end was "now" for the old start; for a new one it is just stale, so the fresh suggestion takes its place.
    if (fromNow) { setFromNow(false); setEndTime(''); setLength(''); return; }
    // A typed end stays put and the length follows it: End is what gets saved, so Length must never disagree.
    const s = at(nextDate, nextTime), e = s && endTime ? endAfter(s, nextDate, endTime) : null;
    if (s && e) setLength(lengthOf(s, e));
  };

  // Most sessions added by hand end "just now", so an empty End and Length show what that would be. "Now" is read
  // when the dialog opens and when the start changes, not ticked: a hint that drifts while being read is harder to
  // copy. A start more than a day back has no meaningful "now" to end at; those keep the format examples.
  const sinceStart = start ? Math.floor(now.getTime() / 60_000) * 60_000 - start.getTime() : 0; // whole minutes, so Length agrees with End
  const untilNow = sinceStart >= 60_000 && sinceStart < DAY_MS ? now : null;
  // The check inside Length takes the greyed suggestion as it stands; once either field holds a value it has nothing to offer.
  const suggestsNow = untilNow !== null && !length;

  // Live: re-evaluates as the user types and if another session changes underneath the dialog.
  const overlaps = useLiveQuery(() => (start && end ? findOverlaps(start, end, session?.id) : Promise.resolve([] as Session[])), [start?.getTime(), end?.getTime(), session?.id], [] as Session[]) ?? [];

  // A goal with a project is a task, as it is on the timer: that is what lets the timer offer it again tomorrow.
  // The task held (an untouched edit's, even if since finished, or a picked one) stays; anything else finds or makes one.
  const taskIdFor = async () => {
    if (kind === 'break' || !project || !goal.trim()) return null;
    if (taskId) return taskId;
    return (await findOrCreateTask(project.id, goal, labelIds)).id;
  };
  const input = (s: Date, e: Date, taskId: string | null): SessionInput => ({ mode: modeOf(kind), startedAt: s, endedAt: e, goalText: kind === 'break' ? '' : goal, projectId: kind === 'break' ? null : project?.id ?? null, taskId, labelIds: kind === 'break' ? [] : labelIds });

  const save = async (resolution: 'trim-others' | 'shorten-this' = 'trim-others') => {
    if (!start || !end) return setError('Give the session a start and either an end or a length.');
    if (end.getTime() - start.getTime() < 60_000) return setError('A session needs at least a minute.');
    if (start > new Date()) return setError('That start is in the future.');
    let s = start, e = end;
    if (overlaps.length && resolution === 'shorten-this') ({ start: s, end: e } = shortenToFit(start, end, overlaps));
    if (overlaps.length && resolution === 'trim-others') for (const o of overlaps) await trimAround(o, start, end);
    const row = input(s, e, await taskIdFor());
    if (session) await updateSession(session, row); else await createManualSession(row);
    finish();
  };
  // Save and Cancel end the draft; any other way out keeps it, unless nothing was touched (then the next Add
  // starts from its own day and time rather than this one's).
  const finish = () => { if (!session) addDraft = null; onOpenChange(false); };
  const dismiss = () => {
    const now: Draft = { goal, taskId, projectId: project?.id ?? null, kind, date, startTime, endTime, length, labelIds };
    if (!session && JSON.stringify(now) !== JSON.stringify(init)) addDraft = now;
    onOpenChange(false);
  };

  const first = overlaps[0];
  return (
    <Modal open onOpenChange={(o) => { if (!o) dismiss(); }} title={session ? 'Edit session' : 'Add session'} size="lg"
      footer={<><Button variant="secondary" size="dialog" onClick={finish}>Cancel</Button><Button variant="primary" size="dialog" onClick={() => void save()}>{overlaps.length ? 'Save & trim the other' : 'Save'}</Button></>}>
      <form className="grid gap-5" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <Field label="Type">{() => <SegmentedControl aria-label="Session type" value={kind} onChange={setKind} options={KINDS} size="sm" />}</Field>
        {kind !== 'break' && (
          <Field label="Goal">{(id) => (
            <div className="flex gap-2 items-center">
              {/* Add session leaves focus to the dialog: an empty goal lists tasks the moment it has focus, and that list
                  should come when asked for, not over the form as it opens. An edit's goal is written, so it waits anyway. */}
              <GoalField id={id} autoFocus={!!session} value={goal} onChange={typeGoal} project={project} onPick={pickTask} />
              {projects.length > 0 && <ProjectChip projects={projects} value={project} onChange={changeProject} allowNone size="field" />}
            </div>
          )}</Field>
        )}
        {/* Date plus a time on the left, a time plus "or" plus a length on the right: the left needs a little more room.
            The lg dialog, so the right column fits both of its 112px fields, and a length like "12h 30m" is never cut off. */}
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-[10px] items-start">
          <Field label="Date & start">{(id) => (
            <div className="flex gap-2"><DateField id={id} value={date} onChange={(v) => moveStart(v, startTime)} className="flex-1 min-w-0" inputClassName="h-10 text-[13px]" /><TimeField value={startTime} onChange={(v) => moveStart(date, v)} aria-label="Start time" inputClassName="h-10 text-[13px]" /></div>
          )}</Field>
          <Field label="End or length" hint={`Fill either one - the other follows. ${time(new Date(2026, 0, 1, 11, 26))} and 74m both work.`}>{(id) => (
            <div className="flex gap-2 items-center"><TimeField id={id} value={endTime} onChange={onEndChange} suggest={untilNow ? format(untilNow, 'HH:mm') : undefined} aria-label="End time" inputClassName="h-10 text-[13px]" /><span className="text-ink-3">or</span><div className="relative w-[112px] shrink-0">
              <Input mono value={length} onChange={(e) => onLengthChange(e.target.value)} placeholder={untilNow ? formatDuration(sinceStart / 1000) : '1h 14m'} aria-label="Length" className={cn('h-10 text-[13px]', suggestsNow && 'pr-9')} />
              {suggestsNow && <Tooltip label="Until now"><FieldButton aria-label={`Until now (${formatDuration(sinceStart / 1000)})`} onClick={() => { onEndChange(format(untilNow, 'HH:mm')); setFromNow(true); }}><Check size={15} /></FieldButton></Tooltip>}
            </div></div>
          )}</Field>
        </div>
        {kind !== 'break' && <Field label="Labels">{() => <LabelPicker value={labelIds} onChange={setLabelIds} project={project} />}</Field>}
        {first && (
          <div className="rounded-input bg-notice-bg text-notice-fg text-body-sm px-3 py-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>Overlaps <span className="font-bold">{first.goalText || 'a session'}</span> ({timeHM(first.startedAt)}–{timeHM(first.endedAt)}){overlaps.length > 1 ? ` and ${overlaps.length - 1} more` : ''}. Saving trims {overlaps.length > 1 ? 'them' : 'it'} —</span>
            <button type="button" className="underline font-bold bg-transparent border-0 p-0 cursor-pointer text-notice-fg" onClick={() => void save('shorten-this')}>shorten this one instead?</button>
          </div>
        )}
        {error && <p className="text-meta-sm text-danger-fg m-0">{error}</p>}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
